import haversine from 'haversine-distance';

import { BucketRadiusContext, MapState } from 'src/app/interfaces/map.interface';
import { Coords, SearchAggregationBucket } from '../../../../swagger-api';
import { getCalculatedStep, isInNotAggregationZoomRange } from '../../map.helpers';
import MapSettings from '../../map.settings';
import { internalReduce } from './decorators.helpers';
import { SharedResponseProcessorDecorator, SharedType } from './shared-response-processor.decorator';

export class SharedResponseCoordsProcessorDecorator<T> extends SharedResponseProcessorDecorator<T> {
  public constructor(entity: SharedType<T>, state: MapState) {
    super(entity, state);
  }

  public getProcessedItems(): T[] {
    const groupedItems: Map<string, T[]> = this.groupItemsByCoords(this.entity.getProcessedItems());

    let newList: T[] = [];
    groupedItems.forEach((items: T[]) => {
      if (items.length === 1) {
        newList.push(items[0]);
      } else {
        newList = newList.concat(this.getDecoratedItemsCoords(items));
      }
    });

    return newList;
  }

  /**
   * @description Decorates aggregation_key property of search aggregation response result
   *              with generated coords value. Returns the updated list of results.
   * @param { T } results
   * @return { T }
   */
  protected getDecoratedItemsCoords(results: T[]): T[] {
    // TODO reduce duplications in children
    return results;
  }

  /**
   * @description Helps to auto-generate shared a radius for aggregation and non-aggregation
   *              pins by incoming zoom level and count of items.
   *              For not aggregated zoom level 1.8 multiplicator used to adjust distance between pins
   *              to be same as in aggregated zoom level.
   * @param { BucketRadiusContext } context
   * @return { number }
   */
  protected getRadius(context: BucketRadiusContext): number {
    if (isInNotAggregationZoomRange(this.state.zoom)) {
      return Math.round(MapSettings.MAP_SEARCH_CITY_ZOOM_LEVEL_RADIUS / getCalculatedStep(this.state.zoom) / 1.8);
    }
    return this.getAggregatedRadius(context.coords, context.aggregatedItemsCount);
  }

  /**
   * @description Groups search aggregation results by coords value and returns.
   * @param { T[] } results
   * @param getKey
   * @param getMapValue
   * @param propertyToLoop
   * @return { T[] }
   */
  protected groupItemsByCoords(
    results: T[],
    getKey?: (result: T, nestedObject?: SearchAggregationBucket) => string,
    getMapValue?: (result: T, nestedObject?: SearchAggregationBucket) => T,
    propertyToLoop?: string,
  ): Map<string, T[]> {
    return internalReduce<T, Map<string, T[]>>(
      results,
      (acc: Map<string, T[]>, result: T) => {
        if (propertyToLoop) {
          (result[propertyToLoop] as SearchAggregationBucket[]).forEach((object: SearchAggregationBucket) => {
            acc = this.getUpdatedMap(acc, result, getKey, getMapValue, object);
          });
        } else {
          acc = this.getUpdatedMap(acc, result, getKey, getMapValue);
        }

        return acc;
      },
      new Map(),
    );
  }

  // TODO Refactoring: Introduce Parameter Object
  private getUpdatedMap(
    acc: Map<string, T[]>,
    result: T,
    getKey?: (result: T, nestedObject?: SearchAggregationBucket) => string,
    getMapValue?: (result: T, nestedObject?: SearchAggregationBucket) => T,
    nestedObject?: SearchAggregationBucket,
  ): Map<string, T[]> {
    const key: string = getKey(result, nestedObject);

    if (acc.get(key) !== undefined) {
      acc.set(key, [...acc.get(key), getMapValue(result, nestedObject)]);
    } else {
      acc.set(key, [getMapValue(result, nestedObject)]);
    }

    return acc;
  }

  /**
   * @description Calculates tile width depending on current zoom level. Needed to calculate radius for pins
   * distribution. Tile width column gives the map width in degrees of longitude, for a square tile drawn at
   * that zoom level
   * @return { number }
   */
  private getTileWidth(): number {
    return 180 / Math.pow(2, this.state.zoom);
  }

  /**
   * @description Calculates radius for aggregated search results depend on incoming zoom
   *              level and items count. Used calculations, steps and so on are fitted as
   *              the most convenient and optimized for different items count.
   *              Checked on the count of items from 2 to 10 points.
   *              Formula created using this scale calculations:
   *              https://wiki.openstreetmap.org/wiki/Zoom_levels
   * @param { Coords } coords
   * @param { number } aggregatedItemsCount
   * @return { number }
   */
  private getAggregatedRadius(coords: Coords, aggregatedItemsCount: number): number {
    const iconRadius: number = this.getIconRadius(aggregatedItemsCount);
    const tileWidth: number = this.getTileWidth();
    const deltaDegree: number = (1.5 * iconRadius * tileWidth) / 256;
    return haversine(
      { latitude: coords.lat, longitude: coords.lon },
      { latitude: coords.lat, longitude: coords.lon + deltaDegree },
    );
  }

  private getIconRadius(count: number): number {
    return count >= MapSettings.BIG_MARKER_COUNT
      ? MapSettings.BIG_MARKER_SIZE / 2
      : count >= MapSettings.MEDIUM_MARKER_COUNT
        ? MapSettings.MEDIUM_MARKER_SIZE / 2
        : MapSettings.SMALL_MARKER_SIZE / 2;
  }
}
